// src/hooks/asset/useUploadAsset.ts
import { useState, useCallback, useEffect, useMemo } from "react";
import { Asset } from "../../state/types"; // Only Asset is needed now

export interface MediaFile {
  file: File;
  mediaGroup: string;
}

// This type remains the same as it's the contract with the context
export interface UploadState {
  asset: Asset;
  uploadedFiles: MediaFile[];
}

// A helper function to create a default asset, useful for a "create new" flow
const createDefaultAsset = (assetIdentifier: string = 'New Asset'): Asset => ({
  assetIdentifier,
  id: '',
  clientId: '',
  image_dir: '',
  config: '',
  pushed: false,
  ready: false,
  vin: assetIdentifier,
  created_at: new Date().toISOString(),
  exterior: 0,
  interior: 0,
  manual: 0,
  thumbnail: '',
  status: null,
  assetId: assetIdentifier,
  pk: '',
  sk: '',
  imageExterior: [],
  imageManual: [],
  imagePano: [],
  imageBgrPano: [],
  imageFeatured: [],
  imageMask: [],
  imageCrop: [],
  imageUndercarriage: [],
  imageUndercarriageDewarp: [],
  imageBgr: [],
  archived: false,
  processingStatus: "NOT STARTED",
  syndicationStatus: "NOT STARTED",
});

interface UseUploadAssetProps {
  initialAsset?: Partial<Asset>;
  onSave?: (state: UploadState) => Promise<void>;
  onSaveSuccess?: () => void;
}

export const useUploadAsset = ({ initialAsset, onSave, onSaveSuccess }: UseUploadAssetProps) => {
  const [asset, setAsset] = useState<Asset>(() => {
    const defaults = createDefaultAsset(initialAsset?.assetIdentifier);
    return { ...defaults, ...initialAsset };
  });

  const [uploadedFiles, setUploadedFiles] = useState<MediaFile[]>([]);
  const [currentImageType, setCurrentImageType] = useState<string>("imageExterior");
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info" as "info" | "success" | "error",
  });

  const { imageCategories, categoryKeys } = useMemo(() => {
    const categories: Record<string, any[]> = {};
    const keys: string[] = [
      "imageExterior", 
      "imageManual", 
      "imagePano", 
      "imageFeatured", 
      "imageCrop", 
      "imageUndercarriage", 
      "imageBgr"
    ];

    keys.forEach((key) => {
      const images = asset[key as keyof Asset];
      categories[key] = (images && Array.isArray(images)) ? images : [];
    });

    return { imageCategories: categories, categoryKeys: keys };
  }, [asset]); 

  useEffect(() => {
    const defaults = createDefaultAsset(initialAsset?.assetIdentifier);
    setAsset({ ...defaults, ...initialAsset });
    setUploadedFiles([]);
  }, [initialAsset]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    // 1. Check if any files were selected. No changes here.
    if (!event.target.files?.length || !currentImageType) return;

    // 2. Convert the FileList into a standard JavaScript array to easily loop over it.
    const files = Array.from(event.target.files);

    // 3. Prepare all selected files for the upload queue.
    const newFilesForUpload: MediaFile[] = files.map(file => ({
      file,
      mediaGroup: currentImageType,
    }));

    // 4. Create a temporary preview URL for each new image so they can be displayed in the UI.
    const newImagePreviews = files.map(file => ({
      imageUrlCf: URL.createObjectURL(file),
      imageTag: `New Image`,
      isNew: true, 
    }));

    // 5. Update the state with the new files.
    setUploadedFiles((prevFiles) => [...prevFiles, ...newFilesForUpload]);

    // 6. Update the asset state to include the new image previews for the current category.
    setAsset(prevAsset => {
      const currentImages = (prevAsset[currentImageType as keyof Asset] as any[]) || [];
      const newImageList = [...currentImages, ...newImagePreviews];
      return { ...prevAsset, [currentImageType]: newImageList };
    });

    // 7. Clear the input so the user can select the same files again if needed. No changes here.
    event.target.value = '';
  }, [currentImageType]);

  const handleAssetChange = useCallback((updatedAsset: Asset) => {
    setAsset(updatedAsset);
  }, []);

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave({ asset, uploadedFiles });
      setNotification({ open: true, message: "Changes saved successfully", severity: "success" });
      setUploadedFiles([]); // Clear the queue of uploaded files after a successful save
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      setNotification({ open: true, message: "Error saving changes. Please try again.", severity: "error" });
    } finally {
      setIsSaving(false);
    }
  }, [onSave, onSaveSuccess, asset, uploadedFiles]);

  return {
    asset,
    uploadedFiles,
    currentImageType,
    isSaving,
    notification,
    imageCategories, 
    categoryKeys,
    setCurrentImageType,
    handleFileUpload,
    handleAssetChange,
    handleCloseNotification,
    handleSave,
  };
};